"use client";
import { fillPageToken, setAnswer } from "@repo/questionnaire";
import { useMemo, useRef } from "react";
import {
  FlowFooter,
  FlowWizardShell,
  buildJourneyProgress,
} from "@/components/flow";
import { QuestionnaireStepHeading } from "./questionnaire-step-heading";
import { QuestionnaireStepFields } from "./questionnaire-step-fields";
import { FormVisitTracker } from "./form-visit-tracker";
import { collectFormVisitAnswers } from "./form-visit-answers";
import { IntakePageCard } from "./intake-page-card";
import { QuestionnaireReview } from "./questionnaire-review";
import type { QuestionnaireRunnerProps } from "./questionnaire-runner-types";
import { useQuestionnaireState } from "./use-questionnaire-state";
import { useQuestionnaireActions } from "./use-questionnaire-actions";
export type {
  QuestionnaireRunnerProps,
  QuestionnaireSaveResult,
} from "./questionnaire-runner-types";
export { mergeAuthoritativeAnswers } from "./questionnaire-runner-values";
export function QuestionnaireRunner(props: QuestionnaireRunnerProps) {
  const {
    definition,
    title = definition.title,
    titleBadge,
    headerAction,
    onClose,
    closeLabel,
    brand,
    chrome = "default",
    stepIndicator,
    firstStepNote,
    firstStepIntro,
    phoneDefaultCountry,
    intakeToken,
    onUploadFile,
    renderComputed,
    completeLabel = "Submit",
    previewMode = false,
    locale = "en",
    chromeCache = {},
    formVisit,
  } = props;
  const state = useQuestionnaireState(props);
  const {
    flow,
    answers,
    fieldErrors,
    actionError,
    saving,
    currentFields,
    pageField,
    isStartPage,
    reviewStep,
    currentStepIndex,
    handleBack,
    handleEditSection,
    fieldsContainerRef,
    setFieldErrors,
    answersRef,
    updateAnswers,
    scheduleAutosave,
    currentStepIdRef,
    chromeLabel,
  } = state;
  const recordSubmissionRef = useRef<(() => void) | null>(null);
  const { handleContinue, handleStepKeyDown } = useQuestionnaireActions(
    props,
    state,
    () => recordSubmissionRef.current?.(),
  );
  // A single-field step uses the actual question, even when its section title
  // is only a topic such as "Taxes". Multi-field steps keep their section title.
  const singleQuestion =
    currentFields.length === 1 && currentFields[0]?.kind !== "computed_display"
      ? currentFields[0]
      : null;
  const description =
    flow.currentStep.description ?? singleQuestion?.description;
  const indicator =
    stepIndicator?.({
      current: currentStepIndex + 1,
      total: flow.visibleSteps.length,
      stepId: flow.currentStep.id,
      stage: flow.currentStep.stage,
    }) ?? null;
  const journeyProgress = useMemo(
    () =>
      buildJourneyProgress({
        steps: flow.visibleSteps,
        currentStepId: flow.currentStep.id,
      }),
    [flow.currentStep.id, flow.visibleSteps],
  );
  const primaryLabel = saving
    ? previewMode
      ? chromeLabel("Loading…")
      : chromeLabel("Saving…")
    : isStartPage
      ? fillPageToken(
          chromeLabel("Talk with {First}"),
          brand?.name ?? "your broker",
        )
      : flow.nextStep
        ? chromeLabel("Continue")
        : previewMode
          ? completeLabel === "Submit"
            ? chromeLabel("Done")
            : completeLabel
          : chromeLabel(completeLabel);
  return (
    <>
      {formVisit && !previewMode ? (
        <FormVisitTracker
          {...formVisit}
          stepId={flow.currentStep.id}
          steps={flow.visibleSteps.map((step) => ({
            id: step.id,
            title: fillPageToken(
              chromeLabel(step.title),
              brand?.name ?? "your broker",
            ),
          }))}
          definitionVersion={definition.version}
          answerEntries={
            formVisit.formKind === "SELLER"
              ? collectFormVisitAnswers(flow.visibleSteps, answers)
              : undefined
          }
          onSubmitReady={(record) => {
            recordSubmissionRef.current = record;
          }}
        />
      ) : null}
      <FlowWizardShell
        title={title}
        presentation={props.presentation}
        contentWidth={
          chrome === "lead" ||
          (props.presentation && props.presentation !== "overlay")
            ? "narrow"
            : "default"
        }
        progressVariant={
          props.presentation && props.presentation !== "overlay"
            ? "inline"
            : "branded"
        }
        titleBadge={titleBadge}
        headerAction={headerAction}
        onClose={onClose}
        closeLabel={closeLabel}
        brand={brand}
        chrome={chrome}
        stepIndicator={isStartPage ? null : indicator}
        journeyProgress={stepIndicator || isStartPage ? null : journeyProgress}
        stepDescription={
          pageField
            ? fillPageToken(pageField.label, brand?.name ?? "Your broker")
            : (flow.currentStep.description ??
              `Complete ${flow.currentStep.title}.`)
        }
        footer={
          <FlowFooter
            primaryFullWidth={chrome === "lead"}
            hideBack={!flow.previousStep && !props.onBackAtStart}
            backLabel={chromeLabel(
              !flow.previousStep && props.onBackAtStart
                ? (props.initialBackLabel ?? "Back")
                : "Back",
            )}
            onBack={() =>
              flow.previousStep ? void handleBack() : props.onBackAtStart?.()
            }
            backDisabled={saving || props.navigationDisabled}
            primaryLabel={primaryLabel}
            primaryDisabled={
              saving || props.navigationDisabled || props.advanceDisabled
            }
            onPrimary={() => void handleContinue()}
          />
        }
      >
        <div className="flex flex-col gap-6">
          {firstStepIntro &&
          flow.currentStep.id ===
            definition.steps.find((step) =>
              step.fields.some(
                (field) =>
                  field.kind !== "page" && field.kind !== "computed_display",
              ),
            )?.id
            ? firstStepIntro
            : null}
          {pageField ? (
            <IntakePageCard
              page={{
                headline: pageField.label,
                bullets: pageField.bullets,
                showTrust: pageField.showTrust,
              }}
              brand={{
                name: brand?.name ?? "Your broker",
                avatarUrl: brand?.avatarUrl,
                licenseNumber: brand?.licenseNumber,
                logoUrl: brand?.logoUrl,
                logoAlt: brand?.logoAlt,
                licenseBadge: brand?.licenseBadge,
              }}
            />
          ) : (
            <QuestionnaireStepHeading
              props={props}
              state={state}
              question={singleQuestion}
              description={description}
            />
          )}
          {reviewStep ? (
            <QuestionnaireReview
              definition={definition}
              answers={answers}
              onEdit={(stepId) => void handleEditSection(stepId)}
            />
          ) : pageField ? null : (
            <div
              ref={fieldsContainerRef}
              data-questionnaire-fields
              className="space-y-5 sm:space-y-6 [&_input]:scroll-mb-28 [&_textarea]:scroll-mb-28"
              onKeyDown={handleStepKeyDown}
            >
              <QuestionnaireStepFields
                fields={currentFields}
                answers={answers}
                errors={fieldErrors}
                description={description}
                disabled={saving}
                defaultCountry={phoneDefaultCountry}
                intakeToken={intakeToken}
                translations={locale === "en" ? undefined : chromeCache}
                locale={locale}
                onUploadFile={onUploadFile}
                renderComputed={
                  renderComputed
                    ? (field) => renderComputed(field, answers)
                    : undefined
                }
                onChange={(patch, fieldIds = Object.keys(patch)) => {
                  setFieldErrors((current) =>
                    Object.fromEntries(
                      Object.entries(current).filter(
                        ([key]) => !fieldIds.includes(key),
                      ),
                    ),
                  );
                  const next = Object.entries(patch).reduce(
                    (current, [binding, value]) =>
                      setAnswer(current, binding, value),
                    answersRef.current,
                  );
                  answersRef.current = next;
                  updateAnswers(next);
                  scheduleAutosave(currentStepIdRef.current);
                }}
              />
            </div>
          )}
          {firstStepNote && currentStepIndex === 0 ? firstStepNote : null}
          {props.stepNote?.(flow.currentStep.id)}

          {actionError && (
            <p
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {actionError}
            </p>
          )}
        </div>
      </FlowWizardShell>
    </>
  );
}
